import type { Rating } from "../types/rating";
import { isUnknownArray, isTimestamp } from "../types/data";
import type { AchievementExperienceItem } from "../types/experience";
import type { ProgressExperienceItem } from "../types/experience";
import {
  getWeekStart
} from "./weeklyStreakTracker";
import { filterExperienceRatings, isGameProgressEligible } from "./gameProgressEligibility";
import { getMovieRating } from "../components/chartsData/getMovieRating";
import { getCountryCode } from "./countryNames";
import { getPersonIdentity, getRatingDirectors } from "./ratingPeople";


interface AchievementDefinition extends AchievementExperienceItem {
  id: number;
  title: string;
  description: string;
  difficulty: "easy" | "medium" | "hard" | "impossible";
  maxProgress: number;
}
interface QualityAchievement extends AchievementDefinition {
  weekQuality: { rank: "gold" | "platinum" | "diamond" | "legendary"; target: number };
}
export interface AchievementResult extends AchievementDefinition { unlocked: boolean; progress: number; experience: number; }
export interface DungeonAchievementStats { completed?: number; epicCompleted?: number; legendaryCompleted?: number; }
export interface AchievementOptions { referenceDate?: Date; /** Migration only: identify rewards counted by the old rolling calculation. */ useCurrentTimeline?: boolean; legendaryQuestCardsCompleted?: number; completedDungeons?: number; completedEpicDungeons?: number; completedLegendaryDungeons?: number; }

const WEEK_QUALITY_ACHIEVEMENTS: QualityAchievement[] = [
  {
    id: 1001,
    title: "Первая золотая неделя",
    description: "7 фильмов за неделю",
    difficulty: "hard",
    maxProgress: 1,
    weekQuality: { rank: "gold", target: 1 }
  },
  {
    id: 1002,
    title: "Первая платиновая неделя",
    description: "14 фильмов за неделю",
    difficulty: "hard",
    maxProgress: 1,
    weekQuality: { rank: "platinum", target: 1 }
  },
  {
    id: 1003,
    title: "Первая алмазная неделя",
    description: "21 фильм за неделю",
    difficulty: "impossible",
    maxProgress: 1,
    weekQuality: { rank: "diamond", target: 1 }
  },
  {
    id: 1004,
    title: "Первая легендарная неделя",
    description: "28 фильмов за неделю",
    difficulty: "impossible",
    maxProgress: 1,
    weekQuality: { rank: "legendary", target: 1 }
  },
  {
    id: 1005,
    title: "10 золотых недель",
    description: "10 недель с 7+ фильмами",
    difficulty: "hard",
    maxProgress: 10,
    weekQuality: { rank: "gold", target: 10 }
  },
  {
    id: 1006,
    title: "10 платиновых недель",
    description: "10 недель с 14+ фильмами",
    difficulty: "hard",
    maxProgress: 10,
    weekQuality: { rank: "platinum", target: 10 }
  },
  {
    id: 1007,
    title: "10 алмазных недель",
    description: "10 недель с 21+ фильмом",
    difficulty: "impossible",
    maxProgress: 10,
    weekQuality: { rank: "diamond", target: 10 }
  },
  {
    id: 1008,
    title: "10 легендарных недель",
    description: "10 недель с 28+ фильмами",
    difficulty: "impossible",
    maxProgress: 10,
    weekQuality: { rank: "legendary", target: 10 }
  },
  ...(["gold", "platinum", "diamond", "legendary"] as const).flatMap((rank) => {
    const labels = {
      gold: ["золотых", "7+"],
      platinum: ["платиновых", "14+"],
      diamond: ["алмазных", "21+"],
      legendary: ["легендарных", "28+"]
    };
    const baseIds = { gold: 1010, platinum: 1020, diamond: 1030, legendary: 1040 };
    return [50, 100, 500].map((target, index): QualityAchievement => ({
      id: baseIds[rank] + index,
      title: `${target} ${labels[rank][0]} недель`,
      description: `${target} недель с ${labels[rank][1]} фильмами`,
      difficulty: "impossible",
      maxProgress: target,
      weekQuality: { rank, target }
    }));
  })
];

const WEEK_QUALITY_THRESHOLDS = {
  gold: 7,
  platinum: 14,
  diamond: 21,
  legendary: 28
};

export const ACHIEVEMENT_EXPERIENCE_BY_DIFFICULTY = {
  easy: 10,
  medium: 30,
  hard: 80,
  impossible: 400
};

export const getAchievementExperience = (achievementOrDifficulty: string | AchievementExperienceItem | null | undefined) => {
  const explicitExperience = Number(typeof achievementOrDifficulty === "object" ? achievementOrDifficulty?.experience : undefined);
  if (Number.isFinite(explicitExperience) && explicitExperience > 0) {
    return explicitExperience;
  }
  const difficulty = typeof achievementOrDifficulty === "string"
    ? achievementOrDifficulty
    : achievementOrDifficulty?.difficulty;
  return ACHIEVEMENT_EXPERIENCE_BY_DIFFICULTY[difficulty as keyof typeof ACHIEVEMENT_EXPERIENCE_BY_DIFFICULTY] ||
    ACHIEVEMENT_EXPERIENCE_BY_DIFFICULTY.easy;
};

export const getNewDungeonAchievements = (stats: DungeonAchievementStats = {}, rarity = "common") => {
  const achievements: { id: number; title: string; experience: number }[] = [];
  if ((Number(stats.completed) || 0) < 1) {
    achievements.push({
      id: 1201,
      title: "Первая экспедиция",
      experience: getAchievementExperience("hard")
    });
  }
  if (rarity === "epic" && (Number(stats.epicCompleted) || 0) < 1) {
    achievements.push({
      id: 1202,
      title: "Эпический рейд",
      experience: getAchievementExperience("hard")
    });
  }
  if (rarity === "legendary" && (Number(stats.legendaryCompleted) || 0) < 1) {
    achievements.push({
      id: 1203,
      title: "Легендарный рейд",
      experience: getAchievementExperience("impossible")
    });
  }
  return achievements;
};

export const resolveDungeonAchievementStats = ({
  contextStats = {},
  persistedStats = {},
  contextLoaded = true,
  completedDungeons = []
}: { contextStats?: DungeonAchievementStats; persistedStats?: DungeonAchievementStats; contextLoaded?: boolean; completedDungeons?: Pick<ProgressExperienceItem, "rarity">[] } = {}) => {
  // Once the dungeon context has loaded it is authoritative. A profile object
  // is often a cached snapshot and using Math.max with it can keep revoked
  // achievements and XP alive after the live counter was decremented.
  const source = contextLoaded ? contextStats : persistedStats;
  const completed = isUnknownArray(completedDungeons) ? completedDungeons : [];
  return {
    completed: Math.max(
      Number(source?.completed) || 0,
      completed.length
    ),
    epicCompleted: Math.max(
      Number(source?.epicCompleted) || 0,
      completed.filter((item) => item?.rarity === "epic").length
    ),
    legendaryCompleted: Math.max(
      Number(source?.legendaryCompleted) || 0,
      completed.filter((item) => item?.rarity === "legendary").length
    )
  };
};

// Base achievement categories with metadata
const baseAchievementCategories: Record<string, AchievementDefinition[]> = {
  Легкие: [
    {
      id: 1,
      title: "Первый шаг",
      description: "Оценили первый фильм",
      difficulty: "easy",
      maxProgress: 1
    },
    {
      id: 2,
      title: "Начинающий",
      description: "Оценили 10 фильмов",
      difficulty: "easy",
      maxProgress: 10
    },
    {
      id: 3,
      title: "Любитель",
      description: "Оценили 25 фильмов",
      difficulty: "easy",
      maxProgress: 25
    },
    {
      id: 4,
      title: "Перфекционист",
      description: "10 оценок 10/10",
      difficulty: "easy",
      maxProgress: 10
    },
    {
      id: 5,
      title: "Мировой киноман",
      description: "Оценили фильмы из 5 разных стран",
      difficulty: "easy",
      maxProgress: 5
    },
    {
      id: 6,
      title: "Временной путешественник",
      description: "Оценили фильмы из 5 разных десятилетий",
      difficulty: "easy",
      maxProgress: 5
    }
  ],
  Средние: [
    {
      id: 7,
      title: "Киноман",
      description: "Оценили 50 фильмов",
      difficulty: "medium",
      maxProgress: 50
    },
    {
      id: 8,
      title: "Знаток",
      description: "Оценили 75 фильмов",
      difficulty: "medium",
      maxProgress: 75
    },
    {
      id: 9,
      title: "Активный зритель",
      description: "Оценили 10 фильмов за любые 30 дней",
      difficulty: "medium",
      maxProgress: 10
    },
    {
      id: 10,
      title: "Спринт",
      description: "3 фильма за 3 дня",
      difficulty: "medium",
      maxProgress: 3
    },
    {
      id: 11,
      title: "Недельный марафон",
      description: "5 фильмов за неделю",
      difficulty: "medium",
      maxProgress: 5
    },
    {
      id: 12,
      title: "Жанровый гурман",
      description: "Оценили фильмы из 10 разных жанров",
      difficulty: "medium",
      maxProgress: 10
    },
    {
      id: 13,
      title: "Знаток режиссеров",
      description: "Оценили фильмы 10 разных режиссеров",
      difficulty: "medium",
      maxProgress: 10
    },
    {
      id: 14,
      title: "Начинающая серия",
      description: "2 недели подряд с просмотрами",
      difficulty: "medium",
      maxProgress: 2
    },
    {
      id: 28,
      title: "Ежедневный зритель",
      description: "7 дней подряд с просмотрами",
      difficulty: "medium",
      maxProgress: 7
    },
    {
      id: 29,
      title: "Недельный марафонец",
      description: "14 дней подряд с просмотрами",
      difficulty: "medium",
      maxProgress: 14
    }
  ],
  Сложные: [
    {
      id: 15,
      title: "Критик",
      description: "Оценили 100 фильмов",
      difficulty: "hard",
      maxProgress: 100
    },
    {
      id: 16,
      title: "Мастер",
      description: "Оценили 200 фильмов",
      difficulty: "hard",
      maxProgress: 200
    },
    {
      id: 17,
      title: "Эксперт",
      description: "Оценили 500 фильмов",
      difficulty: "hard",
      maxProgress: 500
    },
    {
      id: 18,
      title: "Легенда",
      description: "Оценили 1000 фильмов",
      difficulty: "hard",
      maxProgress: 1000
    },
    {
      id: 20,
      title: "Супер активный",
      description: "20 фильмов за любые 30 дней",
      difficulty: "hard",
      maxProgress: 20
    },
    {
      id: 21,
      title: "Жанровый коллекционер",
      description: "Оценили фильмы из 20 разных жанров",
      difficulty: "hard",
      maxProgress: 20
    },
    {
      id: 22,
      title: "Глобальный зритель",
      description: "Оценили фильмы из 10 разных стран",
      difficulty: "hard",
      maxProgress: 10
    },
    {
      id: 23,
      title: "Режиссерский гурман",
      description: "Оценили фильмы 20 разных режиссеров",
      difficulty: "hard",
      maxProgress: 20
    },
    {
      id: 24,
      title: "Хронолог",
      description: "Оценили фильмы из 10 разных десятилетий",
      difficulty: "hard",
      maxProgress: 10
    },
    {
      id: 25,
      title: "Пять недель подряд",
      description: "5 недель подряд с хотя бы одним просмотром",
      difficulty: "hard",
      maxProgress: 5
    },
    {
      id: 26,
      title: "Мастер серии",
      description: "10 недель подряд с просмотрами",
      difficulty: "hard",
      maxProgress: 10
    },
    {
      id: 27,
      title: "Пятнадцать недель подряд",
      description: "15 недель подряд с просмотрами",
      difficulty: "hard",
      maxProgress: 15
    },
    {
      id: 30,
      title: "Месячный чемпион",
      description: "30 дней подряд с просмотрами",
      difficulty: "hard",
      maxProgress: 30
    },
    {
      id: 1101,
      title: "Легендарная карта",
      description: "Выполнить задание легендарной карты",
      difficulty: "hard",
      maxProgress: 1
    },
    {
      id: 1102,
      title: "Охотник за легендами",
      description: "Выполнить 10 заданий легендарных карт",
      difficulty: "hard",
      maxProgress: 10
    },
    {
      id: 1201,
      title: "Первая экспедиция",
      description: "Завершить недельную соло-экспедицию",
      difficulty: "hard",
      maxProgress: 1
    },
    {
      id: 1202,
      title: "Эпический рейд",
      description: "Завершить эпическую экспедицию",
      difficulty: "hard",
      maxProgress: 1
    }
  ],
  Невозможные: [
    {
      id: 31,
      title: "Легенда кино",
      description: "Оценили 5000 фильмов",
      difficulty: "impossible",
      maxProgress: 5000
    },
    {
      id: 32,
      title: "Хронокино 130",
      description: "1 фильм на каждый год с 1895 по 2025",
      difficulty: "impossible",
      maxProgress: 130
    },
    {
      id: 33,
      title: "Мировой охват",
      description: "193 страны — по 1 фильму с каждой",
      difficulty: "impossible",
      maxProgress: 193
    },
    {
      id: 34,
      title: "Год кино",
      description: "365 фильмов за 365 дней",
      difficulty: "impossible",
      maxProgress: 365
    },
    {
      id: 1103,
      title: "Коллекционер легенд",
      description: "Выполнить 50 заданий легендарных карт",
      difficulty: "impossible",
      maxProgress: 50
    },
    {
      id: 1104,
      title: "Повелитель легендарных карт",
      description: "Выполнить 100 заданий легендарных карт",
      difficulty: "impossible",
      maxProgress: 100
    },
    {
      id: 1203,
      title: "Легендарный рейд",
      description: "Завершить легендарную экспедицию",
      difficulty: "impossible",
      maxProgress: 1
    }
  ]
};

// Function to calculate achievements based on ratings
const calculateAchievements = (ratings: Rating[], options: AchievementOptions = {}): Record<string, AchievementResult[]> => {
  // Deleted ratings remain in the immutable history with active:false. They
  // must not keep achievements unlocked after the current rating is removed.
  const safeRatings = (isUnknownArray(ratings) ? ratings : [])
    .filter(
      (rating: Rating) =>
        rating &&
        typeof rating === "object" &&
        rating.active !== false
    );
  const achievementRatings = filterExperienceRatings(safeRatings);
  ratings = achievementRatings;
  const totalRatings = achievementRatings.length;
  const completedLegendaryQuestCards = Number(options.legendaryQuestCardsCompleted) || 0;
  const completedDungeons = Number(options.completedDungeons) || 0;
  const completedEpicDungeons = Number(options.completedEpicDungeons) || 0;
  const completedLegendaryDungeons = Number(options.completedLegendaryDungeons) || 0;
  const isTimelineProgressEligible = (rating: Rating) => isGameProgressEligible(rating);
  const timelineRatings = ratings.filter(isTimelineProgressEligible);
  const getRatingDate = (rating: Rating) => {
    // firstTrackedAt is the immutable first-save time. ratedAt can be edited
    // later when the user corrects the watched date and must not move progress.
    const value = rating?.firstTrackedAt || rating?.firstRatedAt || rating?.ratedAt;
    if (!value) return null;
    const date = isTimestamp(value) ? value.toDate() : value instanceof Date ? new Date(value) :
      typeof value === "string" || typeof value === "number" ? new Date(value) : new Date(NaN);
    return Number.isNaN(date.getTime()) ? null : date;
  };
  const perfectRatings = ratings.filter((rating: Rating) => {
    const progressRating = rating?.progressUserRate;
    if (progressRating != null && Number.isFinite(Number(progressRating))) {
      return Number(progressRating) === 10;
    }
    return getMovieRating(rating) === 10;
  }).length;

  // An achievement records a historical feat, not a subscription to recent
  // activity. Recompute from surviving immutable first-save dates so time
  // alone never revokes XP, while deleting its evidence still can.
  const referenceDate = options.referenceDate || new Date();
  const timelineDates = timelineRatings.map(getRatingDate)
    .filter((date): date is Date => date !== null && date.getTime() <= referenceDate.getTime())
    .sort((left, right) => left.getTime() - right.getTime());
  const dayMs = 24 * 60 * 60 * 1000;
  const bestWindowCount = (days: number) => {
    if (options.useCurrentTimeline) {
      return timelineDates.filter(date => referenceDate.getTime() - date.getTime() <= days * dayMs).length;
    }
    let left = 0;
    let best = 0;
    timelineDates.forEach((date, right) => {
      while (date.getTime() - timelineDates[left].getTime() > days * dayMs) left += 1;
      best = Math.max(best, right - left + 1);
    });
    return best;
  };
  // Calendar ordinals avoid treating a DST transition as a missing day/week.
  const calendarDay = (date: Date) => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / dayMs;
  const bestRun = (periods: number[], step: number) => {
    if (options.useCurrentTimeline) {
      const available = new Set(periods);
      let end = calendarDay(step === 7 ? getWeekStart(referenceDate) : referenceDate);
      if (step === 7 && !available.has(end)) end -= step;
      let count = 0;
      while (available.has(end)) { count += 1; end -= step; }
      return count;
    }
    const unique = [...new Set(periods)].sort((left, right) => left - right);
    let run = 0;
    let best = 0;
    unique.forEach((period, index) => {
      run = index > 0 && period - unique[index - 1] === step ? run + 1 : 1;
      best = Math.max(best, run);
    });
    return best;
  };
  const ratingsIn30Days = bestWindowCount(30);
  const ratingsIn3Days = bestWindowCount(3);
  const ratingsIn7Days = bestWindowCount(7);
  const ratingsIn365Days = bestWindowCount(365);
  const weeklyStreak = bestRun(timelineDates.map(date => calendarDay(getWeekStart(date))), 7);
  const consecutiveDays = bestRun(timelineDates.map(calendarDay), 1);

  const uniqueGenres = [
    ...new Set(
      ratings.flatMap((rating: Rating) => [
        ...(isUnknownArray(rating.genres) ? rating.genres : []),
        ...(isUnknownArray(rating.filmGenres) ? rating.filmGenres : [])
      ])
        .map((genre) => String(
          typeof genre === "string" ? genre : genre?.name || ""
        ).trim().toLowerCase())
        .filter(Boolean)
    )
  ].length;
  const uniqueCountries = [
    ...new Set(
      ratings.flatMap((rating: Rating) => [
        ...(isUnknownArray(rating.productionCountry) ? rating.productionCountry : []),
        ...(isUnknownArray(rating.originCountry) ? rating.originCountry : []),
        ...(isUnknownArray(rating.productionCountries) ? rating.productionCountries : [])
      ]).map(getCountryCode).filter(Boolean)
    )
  ].length;

  const uniqueDirectors = [
    ...new Set(
      ratings.flatMap((rating: Rating) =>
        getRatingDirectors(rating).map(getPersonIdentity).filter(Boolean)
      )
    )
  ].length;

  const decades = [
    ...new Set(
      ratings
        .map((r) => {
          const dateValue = r.releaseDate || r.first_air_date;
          if (!dateValue) return null;
          try {
            const date = new Date(dateValue);
            if (isNaN(date.getTime())) return null;
            const year = date.getFullYear();
            if (year < 1800 || year > 2100) return null;
            return Math.floor(year / 10) * 10;
          } catch {
            return null;
          }
        })
        .filter((d) => d !== null)
    )
  ].length;

  const weekQualityCounts = (() => {
    const countsByWeek = new Map<string, number>();
    const currentWeekStart = getWeekStart(referenceDate);
    timelineRatings.forEach((rating: Rating) => {
      const date = getRatingDate(rating);
      if (!date) return;
      const weekStart = new Date(date);
      const day = weekStart.getDay();
      const diff = weekStart.getDate() - day + (day === 0 ? -6 : 1);
      weekStart.setDate(diff);
      weekStart.setHours(0, 0, 0, 0);
      if (weekStart >= currentWeekStart) return;
      const key = weekStart.toISOString().slice(0, 10);
      countsByWeek.set(key, (countsByWeek.get(key) || 0) + 1);
    });

    return Array.from(countsByWeek.values()).reduce<Record<string, number>>((counts, weekFilms) => {
      Object.entries(WEEK_QUALITY_THRESHOLDS).forEach(([rank, threshold]) => {
        if (weekFilms >= threshold) counts[rank] += 1;
      });
      return counts;
    }, { gold: 0, platinum: 0, diamond: 0, legendary: 0 });
  })();

  const buildWeekQualityAchievement = (achievement: QualityAchievement) => {
    const current = weekQualityCounts[achievement.weekQuality.rank] || 0;
    const target = achievement.weekQuality.target;
    return {
      ...achievement,
      unlocked: current >= target,
      progress: Math.min(current, target)
    };
  };

  const weekQualityAchievements = WEEK_QUALITY_ACHIEVEMENTS.map(buildWeekQualityAchievement);

  const uniqueYears = [
    ...new Set(
      ratings
        .map((r) => {
          const dateValue = r.releaseDate || r.first_air_date;
          if (!dateValue) return null;
          try {
            const date = new Date(dateValue);
            if (isNaN(date.getTime())) return null;
            const year = date.getFullYear();
            if (year < 1800 || year > 2100) return null;
            return year;
          } catch {
            return null;
          }
        })
        .filter((y) => y !== null)
    )
  ].length;

  // Create achievement categories with unlocked status and progress
  const achievementCategories = {
    Легкие: baseAchievementCategories["Легкие"].map((ach) => {
      let unlocked = false;
      let progress = 0;
      switch (ach.id) {
        case 1:
          unlocked = totalRatings >= 1;
          progress = Math.min(totalRatings, 1);
          break;
        case 2:
          unlocked = totalRatings >= 10;
          progress = Math.min(totalRatings, 10);
          break;
        case 3:
          unlocked = totalRatings >= 25;
          progress = Math.min(totalRatings, 25);
          break;
        case 4:
          unlocked = perfectRatings >= 10;
          progress = Math.min(perfectRatings, 10);
          break;
        case 5:
          unlocked = uniqueCountries >= 5;
          progress = Math.min(uniqueCountries, 5);
          break;
        case 6:
          unlocked = decades >= 5;
          progress = Math.min(decades, 5);
          break;
        default:
          break;
      }
      return { ...ach, unlocked, progress };
    }),
    Средние: [
      ...baseAchievementCategories["Средние"].map((ach) => {
      let unlocked = false;
      let progress = 0;
      switch (ach.id) {
        case 7:
          unlocked = totalRatings >= 50;
          progress = Math.min(totalRatings, 50);
          break;
        case 8:
          unlocked = totalRatings >= 75;
          progress = Math.min(totalRatings, 75);
          break;
        case 9:
          unlocked = ratingsIn30Days >= 10;
          progress = Math.min(ratingsIn30Days, 10);
          break;
        case 10:
          unlocked = ratingsIn3Days >= 3;
          progress = Math.min(ratingsIn3Days, 3);
          break;
        case 11:
          unlocked = ratingsIn7Days >= 5;
          progress = Math.min(ratingsIn7Days, 5);
          break;
        case 12:
          unlocked = uniqueGenres >= 10;
          progress = Math.min(uniqueGenres, 10);
          break;
        case 13:
          unlocked = uniqueDirectors >= 10;
          progress = Math.min(uniqueDirectors, 10);
          break;
        case 14:
          unlocked = weeklyStreak >= 2;
          progress = Math.min(weeklyStreak, 2);
          break;
        case 28:
          unlocked = consecutiveDays >= 7;
          progress = Math.min(consecutiveDays, 7);
          break;
        case 29:
          unlocked = consecutiveDays >= 14;
          progress = Math.min(consecutiveDays, 14);
          break;
        default:
          break;
      }
      return { ...ach, unlocked, progress };
      }),
      ...weekQualityAchievements.filter((achievement) => achievement.difficulty === "medium")
    ],
    Сложные: [
      ...baseAchievementCategories["Сложные"].map((ach) => {
      let unlocked = false;
      let progress = 0;
      switch (ach.id) {
        case 15:
          unlocked = totalRatings >= 100;
          progress = Math.min(totalRatings, 100);
          break;
        case 16:
          unlocked = totalRatings >= 200;
          progress = Math.min(totalRatings, 200);
          break;
        case 17:
          unlocked = totalRatings >= 500;
          progress = Math.min(totalRatings, 500);
          break;
        case 18:
          unlocked = totalRatings >= 1000;
          progress = Math.min(totalRatings, 1000);
          break;
        case 20:
          unlocked = ratingsIn30Days >= 20;
          progress = Math.min(ratingsIn30Days, 20);
          break;
        case 21:
          unlocked = uniqueGenres >= 20;
          progress = Math.min(uniqueGenres, 20);
          break;
        case 22:
          unlocked = uniqueCountries >= 10;
          progress = Math.min(uniqueCountries, 10);
          break;
        case 23:
          unlocked = uniqueDirectors >= 20;
          progress = Math.min(uniqueDirectors, 20);
          break;
        case 24:
          unlocked = decades >= 10;
          progress = Math.min(decades, 10);
          break;
        case 25:
          unlocked = weeklyStreak >= 5;
          progress = Math.min(weeklyStreak, 5);
          break;
        case 26:
          unlocked = weeklyStreak >= 10;
          progress = Math.min(weeklyStreak, 10);
          break;
        case 27:
          unlocked = weeklyStreak >= 15;
          progress = Math.min(weeklyStreak, 15);
          break;
        case 30:
          unlocked = consecutiveDays >= 30;
          progress = Math.min(consecutiveDays, 30);
          break;
        case 1101:
          unlocked = completedLegendaryQuestCards >= 1;
          progress = Math.min(completedLegendaryQuestCards, 1);
          break;
        case 1102:
          unlocked = completedLegendaryQuestCards >= 10;
          progress = Math.min(completedLegendaryQuestCards, 10);
          break;
        case 1201:
          unlocked = completedDungeons >= 1;
          progress = Math.min(completedDungeons, 1);
          break;
        case 1202:
          unlocked = completedEpicDungeons >= 1;
          progress = Math.min(completedEpicDungeons, 1);
          break;
        default:
          break;
      }
      return { ...ach, unlocked, progress };
      }),
      ...weekQualityAchievements.filter((achievement) => achievement.difficulty === "hard")
    ],
    Невозможные: [
      ...baseAchievementCategories["Невозможные"].map((ach) => {
      let unlocked = false;
      let progress = 0;
      switch (ach.id) {
        case 31:
          unlocked = totalRatings >= 5000;
          progress = Math.min(totalRatings, 5000);
          break;
        case 32:
          unlocked = uniqueYears >= 130;
          progress = Math.min(uniqueYears, 130);
          break;
        case 33:
          unlocked = uniqueCountries >= 193;
          progress = Math.min(uniqueCountries, 193);
          break;
        case 34:
          unlocked = ratingsIn365Days >= 365;
          progress = Math.min(ratingsIn365Days, 365);
          break;
        case 1103:
          unlocked = completedLegendaryQuestCards >= 50;
          progress = Math.min(completedLegendaryQuestCards, 50);
          break;
        case 1104:
          unlocked = completedLegendaryQuestCards >= 100;
          progress = Math.min(completedLegendaryQuestCards, 100);
          break;
        case 1203:
          unlocked = completedLegendaryDungeons >= 1;
          progress = Math.min(completedLegendaryDungeons, 1);
          break;
        default:
          break;
      }
      return { ...ach, unlocked, progress };
      }),
      ...weekQualityAchievements.filter((achievement) => achievement.difficulty === "impossible")
    ]
  };

  return Object.fromEntries(
    Object.entries(achievementCategories).map(([category, achievements]) => [
      category,
      achievements.map((achievement) => ({
        ...achievement,
        experience: getAchievementExperience(achievement)
      }))
    ])
  );
};

export { baseAchievementCategories, calculateAchievements };
